/**
 * SubNavigationContainerComponent contains two SubNavigationComponents. One at left and the other at right.
 *
 * The first one is used to show and select main options, the second one is used to show and select secondary options like colors, etc.
 *
 * SubNavigationContainerComponent component controls the switch between both sides.
 *
 * @Author: Jon Lucas
 * @Date:   2017-09-22T17:17:07+02:00
 * @Email:  jon.lucas@mazedia.fr
 * @Last modified by:   Jon Lucas
 * @Last modified time: 2017-09-29T18:04:47+02:00
 */
import {Component, Input} from "@angular/core";
import {Router} from "@angular/router";
import {trigger, state, style, animate, transition} from "@angular/animations";
import {Location} from "@angular/common";
import {AbstractComponent} from "../../abstract-component/abstract.component";
import {AppService} from "../../app.service";
import {BaseDataType} from "../../base-data-type";
import {ServiceLocator} from "../../service-locator";
import {ConfDataType} from "../../conf-data-type";
import {LabelType} from "../../label-type";

type PaneType = "left" | "right";

@Component({
    selector: "app-sub-navigation-container",
    templateUrl: "./sub-navigation-container.component.html",
    styleUrls: ["./sub-navigation-container.component.scss"],
    animations: [
        trigger("slide", [
            state("left", style({transform: "translateX(0)"})),
            state("right", style({transform: "translateX(-50%)"})),
            transition("* => *", animate(300))
        ])
    ]
})
export class SubNavigationContainerComponent extends AbstractComponent {

    public readonly TAG: string = "[SubNavigationContainerComponent]";

    protected router: Router;

    @Input() public lang: string;

    public activePane: PaneType = "left";

    public types: BaseDataType[];

    @Input() public selectedType: BaseDataType;
    public selectedSubType: BaseDataType;

    private subcomponentType: string;

    private _typeId: string;
    private _subtypeId: string;

    public currentOptionsLabel: {fr: string; en: string; es: string;};
    // Used to bind data to the right pane.
    public currentOptions: BaseDataType[];

    public currentSelectionHasOptions: boolean;

    public validateLabel: LabelType;
    public showValidationButton: boolean;

    constructor(private location: Location) {
        super();

        this.router = ServiceLocator.injector.get(Router);
    }

    public get typeId(): string {
        return this._typeId;
    }

    @Input()
    public set typeId(pId: string) {
        this._typeId = pId;

        console.log(this.TAG, "typeId()", pId);

        if (this.types && (this.selectedType === undefined || this.selectedType.id !== this.typeId)) {
            this.selectedType = this.types.find(type => type.id === this.typeId);
            console.log(this.TAG, "set typeId", this.selectedType, this.lang);
        }
    }

    public get subtypeId(): string {
        return this._subtypeId;
    }

    @Input()
    public set subtypeId(pId: string) {
        this._subtypeId = pId;

        console.log(this.TAG, "subtypeId()", pId);

        this.activePane = "left";

        // setTimeout to reset options sub navigation component after transition
        setTimeout(
            () => this.currentOptions = undefined,
            300
        );

        if (!this._subtypeId) {
            this.selectedSubType = undefined;
            return;
        }

        if (this.selectedType && this.subtypeId && (this.selectedSubType === undefined || this.selectedSubType.id !== this.subtypeId)) {
            this.selectedSubType = this.selectedType.values.find(subtype => subtype.id === this.subtypeId);
            // Show or hide left pane validate button.
            this.detectIfShowOptions(this.confDataService.confdata[this.subtypeId], false);
        }
    }

    public goBack(): void {
        this.router.navigate(["/lang", this.lang, this.typeId, ""]);
    }

    public getData(): void {
        super.getData();

        this.appService.getLabel("validate").then(
            label => this.validateLabel = label
        );

        this.appService.getData(AppService.NAVIGATION_MENU_URL).then(
            types => {
                this.types = types;
                // Force values to update selectedType and selectedSubtype
                this.typeId = this.typeId;
                this.subtypeId = this.subtypeId;
            }
        );
    }

    /**
     * Check if selected type contains options, in witch case, we show them.
     * @param pConfData
     */
    private detectIfShowOptions(pConfData: ConfDataType, pSlide: boolean = true): void {
        console.log(this.TAG, "detectIfShowOptions()", pConfData);

        this.currentSelectionHasOptions = false;

        if (!pConfData.values || pConfData.values.length === 0 || typeof(pConfData.values[0]) !== "string") {
            return;
        }

        this.appService.getType(pConfData.apiurl, pConfData.values[0]).then(
            (type: BaseDataType) => {
                if (type === undefined) {
                    return;
                }
                console.log(this.TAG, type.id, type.options);
                if (type.options && type.options.length > 0) {
                    if (pSlide) {
                        this.currentOptionsLabel = type.label;
                        this.currentOptions = type.options;
                        this.activePane = "right";
                    }
                    this.currentSelectionHasOptions = true;
                }
            }
        );
    }

    public onClickValidate(e: MouseEvent): void {
        console.log(this.TAG, "onClickValidate()", e);

        // Manage project-type-valid
        if (this.selectedSubType && this.selectedSubType.id === "project-type") {
            let value = this.confDataService.getElementValues("project-type");
            if (value && value[0] && value[0] !== "projet-goutiere") {
                let options = this.confDataService.getElementOptions("project-type");
                if (options !== undefined) {
                    if (options.length == 2) {
                        this.confDataService.updateElementValues("project-type-valid", ["true"]);
                    }
                }
            }else{
                this.confDataService.updateElementValues("project-type-valid", ["true"]);
            }
        }

        this.goBack();
    }

    public ngOnInit(): void {
        super.ngOnInit();

        // Listen to confIguration update events
        this.confDataService.observable.subscribe(
            value => {

                // Manage validation button for projet-type options for "debord de toit" (#28713)
                // ---------------
                if (this.selectedSubType && this.selectedSubType.id === "project-type") {
                    let value = this.confDataService.getElementValues("project-type");
                    if (value && value[0] && value[0] !== "projet-goutiere") {
                        let options = this.confDataService.getElementOptions("project-type");
                        if (options !== undefined) {
                            if (options.length == 2) {
                                this.showValidationButton = true;
                            } else {
                                this.showValidationButton = false;
                            }
                        } else {
                            this.showValidationButton = false;
                        }
                    }
                }else{
                    this.showValidationButton = true;
                }
                // ---------------

                if (!this.currentOptions && value.apiurl !== undefined) {
                    this.detectIfShowOptions(value);
                }
            }
        );
    }
}




// WEBPACK FOOTER //
// ./src/app/main-left-column/sub-navigation-container/sub-navigation-container.component.ts